import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Platform,
  StatusBar,
  Dimensions,
  Alert,
  ActivityIndicator,
  Modal
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { firestore, auth, logout, restoreAuthSession } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { translations, Language } from '../config/translations';

export const HomeScreen = ({ navigation }: any) => {
  const [groupName, setGroupName] = useState<string>('');
  const [language, setLanguage] = useState<Language>('en');
  const [isLoading, setIsLoading] = useState(true);
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  useEffect(() => {
    checkAuth();
    loadLanguage();
    StatusBar.setBarStyle('light-content');
    if (Platform.OS === 'android') {
      StatusBar.setBackgroundColor('#005575');
    }
  }, []);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const isAuthenticated = await restoreAuthSession();

      if (!isAuthenticated) {
        navigation.reset({
          index: 0,
          routes: [{ name: 'Login' }],
        });
        return;
      }

      await fetchGroupName();
    } catch (error) {
      console.error('Error checking auth:', error);
      navigation.reset({
        index: 0,
        routes: [{ name: 'Login' }],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadLanguage = async () => {
    try {
      const savedLanguage = await AsyncStorage.getItem('selectedLanguage');
      if (savedLanguage) {
        setLanguage(savedLanguage as Language);
      }
    } catch (error) {
      console.error('Error loading language:', error);
    }
  };

  const selectLanguage = async (selectedLanguage: Language) => {
    try {
      await AsyncStorage.setItem('selectedLanguage', selectedLanguage);
      setLanguage(selectedLanguage);
      setShowLanguageModal(false);
    } catch (error) {
      console.error('Error saving language:', error);
    }
  };

  const fetchGroupName = async () => {
    try {
      const groupId = await AsyncStorage.getItem('currentGroupId');
      console.log('Retrieved groupId:', groupId);
      if (!groupId) return;

      const groupRef = doc(firestore, 'groups', groupId);
      const groupDoc = await getDoc(groupRef);
      
      if (groupDoc.exists()) {
        const name = groupDoc.data().groupName || '';
        console.log('Retrieved group name:', name);
        setGroupName(name);
      }
    } catch (error) {
      console.error('Error fetching group name:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigation.reset({
        index: 0,
        routes: [{ name: 'Login' }],
      });
    } catch (error) {
      console.error('Error logging out:', error);
      Alert.alert('Error', 'Could not log out. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FFFFFF" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#005575" />
      <View style={styles.header}>
        <SafeAreaView style={styles.safeTop}>
          <View style={styles.headerContainer}>
            <Text style={styles.groupName}>
              {groupName || 'Geen groepsnaam gevonden'}
            </Text>
            <View style={styles.headerButtons}>
              <TouchableOpacity
                style={styles.languageButton}
                onPress={() => setShowLanguageModal(true)}
              >
                <Text style={styles.languageButtonText}>
                  {language === 'en' ? '🇬🇧' : '🇳🇱'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.logoutButton}
                onPress={handleLogout}
              >
                <Text style={styles.logoutButtonText}>Uitloggen</Text>
              </TouchableOpacity>
            </View>
          </View>
        </SafeAreaView>
      </View>

      <SafeAreaView style={styles.safeArea}>
        <View style={styles.content}>
          <View style={styles.mainContent}>
            <View style={styles.textContainer}>
              <Text style={styles.welcomeText}>
                {translations[language].welcome}
              </Text>
              
              <Text style={styles.description}>
                {translations[language].whatToDo}
              </Text>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.manualButton]}
                onPress={() => navigation.navigate('AppManual')}
              >
                <Ionicons name="book-outline" size={40} color="#FFFFFF" />
                <Text style={styles.buttonText}>{translations[language].appManual.title}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.reportButton]}
                onPress={() => navigation.navigate('CreateReport')}
              >
                <Ionicons name="warning-outline" size={40} color="#FFFFFF" />
                <Text style={styles.buttonText}>
                  {translations[language].createReport.title}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </SafeAreaView>

      <Modal
        visible={showLanguageModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowLanguageModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Language / Kies Taal</Text>
            <TouchableOpacity
              style={styles.languageOption}
              onPress={() => selectLanguage('en')}
            >
              <Text style={styles.languageOptionText}>🇬🇧 English</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.languageOption}
              onPress={() => selectLanguage('nl')}
            >
              <Text style={styles.languageOptionText}>🇳🇱 Nederlands</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.closeModalButton}
              onPress={() => setShowLanguageModal(false)}
            >
              <Text style={styles.closeModalButtonText}>{translations[language].common.close}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#005575',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#005575',
  },
  safeTop: {
    backgroundColor: '#005575',
  },
  safeArea: {
    flex: 1,
    backgroundColor: '#F5F7FA',
  },
  header: {
    backgroundColor: '#005575',
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'android' ? StatusBar.currentHeight : 0,
    paddingBottom: 12,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 44,
  },
  groupName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
    flex: 1,
    textShadowColor: 'rgba(0, 0, 0, 0.25)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  mainContent: {
    flex: 1,
    justifyContent: 'center',
  },
  textContainer: {
    marginBottom: 40,
  },
  welcomeText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#005575',
    marginBottom: 16,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    color: '#666666',
    marginBottom: 32,
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  button: {
    width: '100%',
    maxWidth: 400,
    padding: 24,
    borderRadius: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  manualButton: {
    backgroundColor: '#005575',
  },
  reportButton: {
    backgroundColor: '#FF9800',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '600',
    marginLeft: 16,
  },
  languageButton: {
    padding: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    minWidth: 45,
    alignItems: 'center',
  },
  languageButtonText: {
    fontSize: 18,
  },
  logoutButton: {
    padding: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
  },
  logoutButtonText: {
    color: '#005575',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    width: '80%',
    maxWidth: 300,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 20,
    textAlign: 'center',
  },
  languageOption: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
    marginBottom: 10,
  },
  languageOptionText: {
    fontSize: 16,
    color: '#333',
    textAlign: 'center',
  },
  closeModalButton: {
    marginTop: 10,
    padding: 12,
    backgroundColor: '#005575',
    borderRadius: 8,
  },
  closeModalButtonText: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontWeight: '600',
  },
}); 